<template>
  <div id="leader-rule-modal" class="uk-modal" uk-modal>
    <div class="uk-modal-dialog uk-modal-body">
      <button class="uk-modal-close-default" type="button" uk-close></button>
      <h2 class="uk-modal-title">
        {{ existingRuleForCriterion ? $t('general.update_rule') : $t('general.add_new_rule') }}
        <span v-if="existingRuleForCriterion" class="mode-indicator">( {{$t('general.current_rule')}} )</span>
      </h2>
      
      <!-- Info Message -->
      <div v-if="existingRuleForCriterion && !editingRule" class="info-message">
        <div class="alert alert-info">
          <i class="uil-info-circle"></i>
          {{$t('general.leader_rule_info')}}
        </div>
      </div>
      
      <form @submit.prevent="submitForm" class="add-form">
        <div class="form-group">
          <label for="criterion" class="form-label">{{$t('general.criterion')}}*</label>
          <AppMultiselect
            v-model="formData.type"
            :options="criterionOptions"
            :searchable="false"
            :close-on-select="true"
            :show-labels="false"
            :placeholder="$t('general.choose_criterion')"
            @input="onCriterionChange"
          />
        </div>

        <div class="form-group">
          <label for="value" class="form-label">{{$t('general.data')}}*</label>
          <AppMultiselect
            v-model="formData.value"
            :options="criterionValues"
            :searchable="true"
            :is-multiple="true"
            :show-labels="false"
            :loading="isLoadingValues"
            :placeholder="$t('general.choose_data')"
            track-by="id"
            label="name"
            :disabled="!formData.type || isLoadingValues"
          />
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            {{ $t('general.cancel')}}
          </button>
          <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
            <i class="uil-arrow-right"></i>
            {{ isSubmitting ? (isEditing ? $t('general.updating') : $t('general.saving') ) : (isEditing ? $t('general.update') : $t('general.save')) }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { showError, showSuccess } from "@/utils/alertUtils.js";
import AppMultiselect from "@/view/components/select/app-multiselect/app-multiselect.vue";
import ApiService from "@/core/services/api.service.js";
import SecureLS from "secure-ls";
import leaderBoardMixin from "@/view/company/LeadersTableManagement/mixin/leaderBoardMixin.js";

export default {
  name: "LeaderRuleModal",
  components: { AppMultiselect },
  mixins: [leaderBoardMixin],
  props: {
    editingRule: {
      type: Object,
      default: null
    },
    existingRules: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formData: {
        type: null,
        value: []
      },
      isSubmitting: false,
      isLoadingValues: false,
      criterionValues: [],
      companyId: null,
      existingRuleForCriterion: null // Seçilen kriter için mevcut kural
    };
  },
  computed: {
    userInfo: {
      get: function () {
        return JSON.parse(this.secureLocalStorage("user"));
      },
      set: function (newValue) {
      },
    },
    
    currentUserCompanyId() {
      return this.userInfo?.company_id || null;
    },

    isEditing() {
      return !!this.editingRule || !!this.existingRuleForCriterion;
    },
  },
  methods: {
    secureLocalStorage(key) {
      try {
        const ls = new SecureLS({ encodingType: "aes", isCompression: true });
        return ls.get(key);
      } catch (error) {
        return null;
      }
    },
    
    openModal(rule = null) {
      this.resetForm();
      this.companyId = this.currentUserCompanyId;
      
      // Eğer düzenleme modundaysa, kural verilerini yükle
      if (rule) {
        this.loadRuleData(rule);
      }
      
      UIkit.modal('#leader-rule-modal').show();
    },

    closeModal() {
      UIkit.modal('#leader-rule-modal').hide();
      this.resetForm();
      this.$emit('modal-closed');
    },

    resetForm() {
      this.formData = {
        type: null,
        value: []
      };
      this.isSubmitting = false;
      this.isLoadingValues = false;
      this.criterionValues = [];
      this.existingRuleForCriterion = null;
    },

    // Kural verilerini form'a yükle (düzenleme için)
    async loadRuleData(rule) {
      // Kriter tipini ayarla
      this.formData.type = this.criterionOptions.find(option => option.id === rule.type);
      
      // onCriterionChange zaten gerekli işlemleri yapacak
      await this.onCriterionChange();
    },

    async onCriterionChange() {
      const selectedType = this.formData.type?.id || this.formData.type;
      
      if (!selectedType) {
        this.criterionValues = [];
        this.formData.value = [];
        this.existingRuleForCriterion = null;
        return;
      }

      // Form'u temizle ve loading başlat
      this.formData.value = [];
      this.criterionValues = [];
      this.isLoadingValues = true;
      this.existingRuleForCriterion = null;

      try {
        this.criterionValues = await this.fetchCriterionValues(selectedType, this.companyId);
        
        // Sonra mevcut kural kontrol et
        const existingRule = this.existingRules.find(rule => rule.type === selectedType);
        
        if (existingRule) {
          this.existingRuleForCriterion = existingRule;
          
          // Mevcut kuralın verilerini seç
          if (existingRule.data && Array.isArray(existingRule.data)) {
            this.formData.value = this.criterionValues.filter(item => 
              existingRule.data.includes(item.id)
            );
          }
        } else {
          console.info('🆕 Yeni kural oluşturulacak - EKLEME MODU:', selectedType);
        }
        
        await this.$nextTick();
      } catch (error) {
        showError('Veriler yüklenirken hata oluştu');
        console.error('Criterion values fetch error:', error);
      } finally {
        this.isLoadingValues = false;
      }
    },

    async submitForm() {
      if (this.isSubmitting) return;
      
      const selectedType = this.formData.type?.id || this.formData.type;
      
      let selectedValues = [];
      if (Array.isArray(this.formData.value)) {
        selectedValues = this.formData.value.map(item => item.id || item);
      } else if (this.formData.value) {
        selectedValues = [this.formData.value.id || this.formData.value];
      }
      
      if (!selectedType) {
        showError(this.$t('general.choose_criterion'));
        return;
      }
      
      if (selectedValues.length === 0) {
        showError(this.$t('general.choose_rule_data'));
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        let response;
        let submitData;
        
        // Mevcut kural varsa güncelle, yoksa ekle
        if (this.existingRuleForCriterion) {
          // GÜNCELLEME: Tek obje formatı
          submitData = {
            type: selectedType,
            data: selectedValues
          };

          response = await ApiService.put(`api/leader-board-rules/${this.existingRuleForCriterion.id}/`, submitData);
        } else {
          // EKLEME: Array formatı
          submitData = [
            {
              type: selectedType,
              data: selectedValues
            }
          ];

          response = await ApiService.post('api/leader-board-rules/', submitData);
        }

        if (response && response.status) {
          const successMessage = this.existingRuleForCriterion ? this.$t('general.rule_update_success')  : this.$t('general.rule_add_success');
          showSuccess(successMessage);
          this.closeModal();
          
          if (this.existingRuleForCriterion) {
            this.$emit('rule-updated');
          } else {
            this.$emit('rule-added');
          }
        } else {
          const errorMessage = response?.data?.message || (this.existingRuleForCriterion ? 'Kural güncellenirken bir hata oluştu' : 'Kural eklenirken bir hata oluştu');
          showError(errorMessage);
        }
      } catch (error) {
        console.error('API Hatası:', error);
        showError('API bağlantısında hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
      } finally {
        this.isSubmitting = false;
      }
    }
  }
}
</script>

<style scoped>
/* Modal Styles */
.uk-modal-dialog {
  max-width: 600px !important;
  width: 90% !important;
  border-radius: 8px;
}

.uk-modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.mode-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: #007bff;
  background: #e3f2fd;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #bbdefb;
}

.add-form {
  padding: 20px 0;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #6c757d;
  font-size: 14px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #6c757d;
  border: 1px solid #e9ecef;
}

.btn-secondary:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #dee2e6;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

/* Multiselect Styles - Simplified */
::v-deep .multiselect {
  min-height: 45px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  color: #333;
  transition: border-color 0.2s;
}

::v-deep .multiselect:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

::v-deep .multiselect__tags {
  min-height: 45px;
  padding: 8px 40px 0 12px;
  border: none;
  background: transparent;
}

::v-deep .multiselect__placeholder {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 8px;
  padding-top: 0;
}

::v-deep .multiselect__single {
  background: transparent;
  padding: 0;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

::v-deep .multiselect__input {
  background: transparent;
  border: none;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  outline: none;
}

::v-deep .multiselect__input::placeholder {
  color: #6c757d;
}

::v-deep .multiselect__content-wrapper {
  border: 1px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 6px 6px;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
}

::v-deep .multiselect__option {
  padding: 12px 16px;
  font-size: 14px;
  color: #333;
  background: white;
  border: none;
  cursor: pointer;
}

::v-deep .multiselect__option--highlight {
  background: #007bff;
  color: white;
}

::v-deep .multiselect__option--selected {
  background: #e3f2fd;
  color: #1976d2;
}

::v-deep .multiselect__spinner {
  border-color: #007bff transparent transparent;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

::v-deep .multiselect__spinner:before {
  border-top-color: #007bff;
}

::v-deep .multiselect__clear {
  background: transparent;
  color: #6c757d;
  font-size: 16px;
  padding: 8px;
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  cursor: pointer;
}

::v-deep .multiselect__clear:hover {
  background: #f8f9fa;
  color: #dc3545;
}

::v-deep .multiselect__caret {
  border-color: #6c757d transparent transparent;
  border-width: 5px 5px 0;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
}

::v-deep .multiselect__caret::before {
  border-top-color: #6c757d;
}

::v-deep .multiselect--disabled {
  background-color: #f8f9fa;
  opacity: 0.6;
  cursor: not-allowed;
}

::v-deep .multiselect--disabled .multiselect__tags {
  background: transparent;
}

::v-deep .multiselect--disabled .multiselect__input {
  background: transparent;
}

/* Remove any unwanted borders or lines */
::v-deep .multiselect * {
  box-sizing: border-box;
}

::v-deep .multiselect::before,
::v-deep .multiselect::after {
  display: none;
}

/* Responsive design */
@media (max-width: 768px) {
  .uk-modal-dialog {
    max-width: 95% !important;
    margin: 10px;
  }
  
  .modal-footer {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}

/* Info Message Styles */
.info-message {
  margin-bottom: 20px;
}

.alert {
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alert-info {
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
}

.alert-info i {
  font-size: 16px;
}

.alert-info strong {
  font-weight: 600;
}
</style> 